import {
  getAnalysisParameterDefinitions,
  normalizeAnalysisParameters
} from './analysisParameterHelpers';

const toText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const createDefaultServiceRow = () => ({
  service: '',
  quantity: '',
  description: '',
  spec: '',
  price: '',
  subtotal: 0
});

const createDefaultServiceGroup = (category = '請選擇服務類別') => ({
  category,
  services: [createDefaultServiceRow()],
  libraryType: '無',
  seqSpec: ''
});

const createDefaultLibrarySampleRow = () => ({
  no: 1,
  sampleName: '',
  tubeLabel: '',
  conc: '',
  vol: '',
  ngsConc: '',
  expectedSeq: '',
  note: ''
});

const createDefaultLibraryDetailRow = () => ({
  no: 1,
  sampleName: '',
  libraryPrepKit: '',
  indexAdapterKit: '',
  setWellPosition: '',
  index1Seq: '',
  index2Seq: '',
  note: '',
  library: '',
  tubeName: ''
});

const createDefaultSampleInfoRow = () => ({
  no: 1,
  sampleName: '',
  sampleBarcode: '',
  fastqName: '',
  vdjType: '',
  bcrFastqName: '',
  tcrFastqName: '',
  tubeLabel: '',
  expectedSeq: '',
  conc: '',
  vol: '',
  ratio260280: '',
  ratio260230: '',
  dqnRqn: '',
  note: ''
});

const normalizeServiceRow = (row = {}) => ({
  ...createDefaultServiceRow(),
  ...row,
  service: toText(row.service ?? row.serviceName),
  quantity: row.quantity === null || row.quantity === undefined ? '' : String(row.quantity),
  description: row.description ?? '',
  spec: row.spec ?? '',
  price: row.price === null || row.price === undefined ? '' : String(row.price),
  subtotal: row.subtotal === null || row.subtotal === undefined ? 0 : row.subtotal,
  sample_count: row.sample_count === null || row.sample_count === undefined
    ? (row.sampleCount === null || row.sampleCount === undefined ? '' : String(row.sampleCount))
    : String(row.sample_count)
});

const normalizeServiceGroup = (group = {}) => {
  const services = Array.isArray(group.services) && group.services.length > 0
    ? group.services.map(normalizeServiceRow)
    : [createDefaultServiceRow()];

  return {
    category: toText(group.category) || '請選擇服務類別',
    services,
    libraryType: toText(group.libraryType ?? group.library_type) || '無',
    seqSpec: toText(group.seqSpec ?? group.seq_spec)
  };
};

const normalizeLibrarySampleRow = (row = {}, index = 0) => ({
  ...createDefaultLibrarySampleRow(),
  ...row,
  no: row.no === null || row.no === undefined ? index + 1 : row.no,
  sampleName: toText(row.sampleName ?? row.sample_name),
  tubeLabel: toText(row.tubeLabel ?? row.tube_label),
  conc: row.conc ?? '',
  vol: row.vol ?? '',
  ngsConc: row.ngsConc ?? row.ngs_conc ?? '',
  expectedSeq: row.expectedSeq ?? row.expected_seq ?? '',
  note: row.note ?? ''
});

const normalizeLibraryDetailRow = (row = {}, index = 0) => ({
  ...createDefaultLibraryDetailRow(),
  ...row,
  no: row.no === null || row.no === undefined ? index + 1 : row.no,
  sampleName: toText(row.sampleName ?? row.sample_name),
  libraryPrepKit: toText(row.libraryPrepKit ?? row.library_prep_kit),
  indexAdapterKit: toText(row.indexAdapterKit ?? row.index_adapter_kit),
  setWellPosition: toText(row.setWellPosition ?? row.set_well_position),
  index1Seq: toText(row.index1Seq ?? row.index1_seq),
  index2Seq: toText(row.index2Seq ?? row.index2_seq),
  note: row.note ?? '',
  library: toText(row.library),
  tubeName: toText(row.tubeName ?? row.tube_name)
});

const normalizeSampleInfoRow = (row = {}, index = 0) => ({
  ...createDefaultSampleInfoRow(),
  ...row,
  no: row.no === null || row.no === undefined ? index + 1 : row.no,
  sampleName: toText(row.sampleName ?? row.sample_name),
  sampleBarcode: toText(row.sampleBarcode ?? row.sample_barcode),
  // SC04：fastqName 是 GEX 的 fastq 檔名前綴，另外三欄記錄配對的 V(D)J library。
  fastqName: toText(row.fastqName ?? row.fastq_name),
  vdjType: toText(row.vdjType ?? row.vdj_type),
  bcrFastqName: toText(row.bcrFastqName ?? row.bcr_fastq_name),
  tcrFastqName: toText(row.tcrFastqName ?? row.tcr_fastq_name),
  // ChIP-seq：沿用以前 PO 的樣本記錄來源 PO 單號；非 ChIP-seq 的單送出時會拿掉。
  sampleSource: toText(row.sampleSource ?? row.sample_source),
  tubeLabel: toText(row.tubeLabel ?? row.tube_label),
  expectedSeq: row.expectedSeq ?? row.expected_seq ?? '',
  conc: row.conc ?? '',
  vol: row.vol ?? '',
  ratio260280: row.ratio260280 ?? '',
  ratio260230: row.ratio260230 ?? '',
  dqnRqn: row.dqnRqn ?? row.dqn_rqn ?? '',
  note: row.note ?? ''
});

const normalizeSelectedServiceCategories = (data = {}) => {
  const explicitCategories = Array.isArray(data.selectedServiceCategories)
    ? data.selectedServiceCategories.map(toText).filter(Boolean)
    : [];

  if (explicitCategories.length > 0) {
    return [...new Set(explicitCategories)];
  }

  if (Array.isArray(data.serviceItems) && data.serviceItems.length > 0) {
    return [...new Set(
      data.serviceItems
        .map(item => toText(item?.category))
        .filter(category => category && category !== '請選擇服務類別')
    )];
  }

  return [];
};

const normalizeServiceItems = (data = {}, selectedServiceCategories = []) => {
  if (Array.isArray(data.serviceItems) && data.serviceItems.length > 0) {
    return data.serviceItems.map(normalizeServiceGroup);
  }

  if (selectedServiceCategories.length > 0) {
    return selectedServiceCategories.map(category => createDefaultServiceGroup(category));
  }

  return [createDefaultServiceGroup()];
};

const toPositiveInteger = value => {
  const text = toText(value);
  if (!/^[1-9]\d*$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isSafeInteger(parsed) ? parsed : null;
};

const resolveRestoredAnalysisSampleCount = (data, selectedServiceCategories, serviceItems) => {
  const isAnalysisOnly = selectedServiceCategories.length === 1
    && selectedServiceCategories[0] === '分析服務 (A)';
  if (!isAnalysisOnly || data.isDraft === true) return data.sampleCount ?? '';

  const explicitSampleCount = toPositiveInteger(data.sampleCount);
  if (explicitSampleCount !== null) return data.sampleCount;

  const serviceSampleCounts = serviceItems
    .flatMap(item => (Array.isArray(item?.services) ? item.services : []))
    .map(service => toPositiveInteger(service?.sample_count))
    .filter(value => value !== null);
  if (serviceSampleCounts.length > 0) {
    return String(Math.max(...serviceSampleCounts));
  }

  const sampleRows = Array.isArray(data.sampleInfo?.sampleSheet)
    ? data.sampleInfo.sampleSheet
    : [];
  return sampleRows.length > 0 ? String(sampleRows.length) : '';
};

const normalizeSampleSheet = (rows = [], rowNormalizer) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [rowNormalizer({}, 0)];
  }

  return rows.map((row, index) => rowNormalizer(row, index));
};

const normalizeScRNAExperimentInfo = (data = {}, findScRNAExperimentOption = null) => {
  const serviceName = toText(
    data.scRNAExperimentInfo?.serviceName
    || data.serviceName
  );

  const descriptionFromPayload = toText(data.scRNAExperimentInfo?.description);
  let description = descriptionFromPayload;

  if (!description && serviceName && typeof findScRNAExperimentOption === 'function') {
    const option = findScRNAExperimentOption(toText(data.platform_type), serviceName);
    description = toText(option?.description);
  }

  return {
    serviceName,
    description
  };
};

const inferSpeciesSelection = (data = {}) => {
  const explicitSelection = toText(data.speciesSelection);
  if (explicitSelection && explicitSelection !== '物種請選擇') {
    return explicitSelection;
  }

  const speciesCommon = toText(data.speciesCommon || data.species);
  const speciesOther = toText(data.speciesOther);

  if (speciesCommon) {
    return speciesCommon;
  }

  if (speciesOther) {
    return '其他';
  }

  return '物種請選擇';
};

export const createDefaultOrderFormData = (overrides = {}) => ({
  salesCode: '',
  customerCode: '',
  salesPerson: '',
  organization: '',
  principalInvestigator: '',
  contactPerson: '',
  contactPhone: '',
  email: '',
  address: '',
  invoiceTitle: '',
  taxId: '',
  invoiceCopies: '二聯式',
  dataDeliveryMethod: 'HDD由專人遞送',
  nchcAccount: '',
  deliveryAddress: '',
  recipient: '',
  recipientPhone: '',
  recipientEmail: '',
  isUrgent: false,
  sampleReturn: '不需要',
  selectedPackage: '',
  packageMultiplier: 1,
  packageQuantity: 1,
  selectedServiceCategories: [],
  serviceItems: [createDefaultServiceGroup()],
  sampleType: '無送樣',
  sampleTypeOther: '',
  libraryInfo: {
    concMethod: '',
    sampleSheet: [createDefaultLibrarySampleRow()],
    runConfig: {
      sequencer: '不限',
      read1Length: '151bp',
      read2Length: '151bp',
      phiX: '1%'
    },
    librarySampleSheet: [createDefaultLibraryDetailRow()],
    gelImage: ''
  },
  sampleInfo: {
    concMethod: '',
    sampleSheet: [createDefaultSampleInfoRow()]
  },
  preservationMethod: 'Nuclease-free H2O',
  preservationMethodOther: '',
  sampleCount: '',
  species: '',
  speciesSelection: '物種請選擇',
  speciesSelectionKey: '',
  speciesCommon: '',
  speciesScientific: '',
  speciesGenome: '',
  speciesOther: '',
  TenXGenomicsRefVersion: '',
  TenXGenomicsProbeSetVersion: '',
  TenXGenomicsVDJVersion: '',
  shippingMethod: '冷凍(乾冰)',
  shippingMethodOther: '',
  scRNAExperimentInfo: {
    serviceName: '',
    description: ''
  },
  notes: '',
  signature: null,
  ...overrides
});

/**
 * 舊單 / 草稿的「分析參數」：只留目前品項適用的參數，不合法的數字或選項退回預設值，
 * 抗體沒填就維持空白（要使用者自己選）。這裡只做整理，不會因為抗體去改 peak calling mode。
 * 品項沒有分析參數時不動原本的資料，送出時會被拿掉。
 */
const normalizeRestoredAnalysisParameters = (orderData = {}, serviceItems = []) => {
  const definitions = getAnalysisParameterDefinitions({ serviceItems });
  if (definitions.length === 0) return {};

  return {
    analysisParameters: normalizeAnalysisParameters(definitions, orderData.analysisParameters)
  };
};

export const normalizeRestoredOrderData = (orderData = {}, {
  findScRNAExperimentOption = null
} = {}) => {
  const selectedServiceCategories = normalizeSelectedServiceCategories(orderData);
  const serviceItems = normalizeServiceItems(orderData, selectedServiceCategories);
  const sampleType = toText(orderData.sampleType) || '無送樣';
  const speciesSelection = inferSpeciesSelection(orderData);
  const sampleCount = resolveRestoredAnalysisSampleCount(
    orderData,
    selectedServiceCategories,
    serviceItems
  );

  return {
    ...createDefaultOrderFormData(),
    ...orderData,
    selectedServiceCategories,
    serviceItems,
    sampleInfo: {
      ...(orderData.sampleInfo || {}),
      concMethod: toText(orderData.sampleInfo?.concMethod),
      sampleSheet: normalizeSampleSheet(
        orderData.sampleInfo?.sampleSheet,
        normalizeSampleInfoRow
      )
    },
    libraryInfo: {
      ...(orderData.libraryInfo || {}),
      concMethod: toText(orderData.libraryInfo?.concMethod),
      sampleSheet: normalizeSampleSheet(
        orderData.libraryInfo?.sampleSheet,
        normalizeLibrarySampleRow
      ),
      runConfig: {
        sequencer: toText(orderData.libraryInfo?.runConfig?.sequencer) || '不限',
        read1Length: toText(orderData.libraryInfo?.runConfig?.read1Length) || '151bp',
        read2Length: toText(orderData.libraryInfo?.runConfig?.read2Length) || '151bp',
        phiX: toText(orderData.libraryInfo?.runConfig?.phiX) || '1%'
      },
      librarySampleSheet: normalizeSampleSheet(
        orderData.libraryInfo?.librarySampleSheet,
        normalizeLibraryDetailRow
      ),
      gelImage: orderData.libraryInfo?.gelImage ?? ''
    },
    sampleType,
    sampleCount,
    speciesSelection,
    speciesSelectionKey: toText(orderData.speciesSelectionKey),
    species: orderData.species ?? '',
    speciesCommon: toText(orderData.speciesCommon || orderData.species || orderData.speciesOther),
    speciesScientific: toText(orderData.speciesScientific),
    speciesGenome: toText(orderData.speciesGenome),
    speciesOther: toText(orderData.speciesOther),
    TenXGenomicsRefVersion: toText(orderData.TenXGenomicsRefVersion),
    TenXGenomicsProbeSetVersion: toText(orderData.TenXGenomicsProbeSetVersion),
    TenXGenomicsVDJVersion: toText(orderData.TenXGenomicsVDJVersion),
    scRNAExperimentInfo: normalizeScRNAExperimentInfo(orderData, findScRNAExperimentOption),
    ...normalizeRestoredAnalysisParameters(orderData, serviceItems),
    notes: orderData.notes ?? '',
    signature: orderData.signature ?? null
  };
};
